import React, { useState, useEffect } from 'react';
import { getSalesOffices, deleteProjectSalesOffices } from '../api/api';
import { useAuthentication } from '../context/AuthenticationContext.tsx';
import { SalesOffice } from '../types.ts';
import Icon from '../../components/common/Icon';
import ConfirmationPrompt from '../components/ConfirmationPrompt.tsx';
import { trackSalesOfficeInteraction, SalesOfficeInteractionType } from '../analytics';
import { trackFindSalesOfficeClick } from '../../analytics/my-dashboard';
import toast from 'react-hot-toast';
import { text } from '../data';
import DOMPurify from 'dompurify';

interface FormmattedSalesOffice {
	address: string;
	category: string;
	coordinates: string;
	directionsUrl: string;
	email: string;
	isActive: string;
	markerId: string;
	lat: string;
	lng: string;
	phone: string;
	salesOfficeId: string;
	salesOfficeName: string;
	salesOfficeNumber: string;
}

const MySalesOffice: React.FC = () => {
	const apiKeyString = window?._timken?.googleApiKey as string ?? '';
	const salesOfficeUrl = window?._timken?.salesOfficeUrl as string ?? 'https://locations.timken.com/sales-locator/';

	const { isLoggedIn, idToken } = useAuthentication();
	const [ salesOffice, setSalesOffice ] = useState<FormmattedSalesOffice | null>(null);
	const [ loading, setLoading ] = useState(true);
	const [ isConfirmOpen, setIsConfirmOpen ] = useState(false);
	const [ confirmationMessage, setConfirmationMessage ] = useState('');
	const [ removalMessage, setRemovalMessage ] = useState('');

	useEffect(() => {
		if (isLoggedIn && !! idToken) {
			fetchSalesOffice();
		}
	}, [ isLoggedIn, idToken ]);

	const fetchSalesOffice = async () => {
		getSalesOffices().then((response) => {
			let saleOffice;

			if (response?.salesOffices.length) {
				saleOffice = response.salesOffices[ 0 ];
			} else {
				console.warn('No sales office found');
			}

			buildSalesOfficeInfo(saleOffice);
		}).catch((error) => {
			console.error('Error fetching sales offices:', error);
		}).finally(() => {
			setLoading(false);
		});
	};

	const triggerDeleteConfirmation = () => {
		if (! salesOffice) {
			return;
		}
		const salesOfficeName = getOfficeSubName();
		setConfirmationMessage(`Are you sure you want to remove the "${ salesOfficeName }"?`);
		setRemovalMessage(`The "${ salesOfficeName }" has been removed`);
		setIsConfirmOpen(true);
	};

	const deleteSalesOffice = async () => {
		setLoading(true);
		try {
			const toastId = toast.loading(text.loading);
			setIsConfirmOpen(false);
			await deleteProjectSalesOffices(salesOffice?.salesOfficeId || '');
			toast.success(`${ removalMessage }`, {
				id: toastId,
				duration: 5000,
			});
			triggerSalesOfficeAnalytics('remove_saved_sales_office_click', text.remove, '');

			await fetchSalesOffice();
		} catch (error) {
			toast.dismiss();
			toast.error(error.message);
		}
		setLoading(false);
	};

	const parseContactInfo = (contactInfo: string) => {
		if (! contactInfo) {
			return {
				email: '',
				phone: '',
			};
		}
		const $tempContainer = document.createElement('div');
		$tempContainer.innerHTML = contactInfo;
		const $distributorLinks = $tempContainer.querySelectorAll('a');
		const contactUrls = [ ...$distributorLinks ].map(($linkItem) => $linkItem.getAttribute('href'));
		const phoneLink = contactUrls.find((url) => url?.indexOf('tel') > -1) ?? '';
		const emailLink = contactUrls.find((url) => url?.indexOf('mailto') > -1) ?? '';
		const phone = phoneLink ? phoneLink?.split(':')[ 1 ] : '';
		const email = emailLink ? emailLink?.split(':')[ 1 ] : '';
		return {
			email,
			phone,
		};
	};

	const buildeDirectionsUrl = (address: string, markerId?: string) => {
		if (! address) {
			return '';
		}
		return `${ salesOfficeUrl }?address=${ address }&marker_id=${ markerId }`;
	};

	const buildSalesOfficeInfo = (targetSalesOffice: SalesOffice) => {
		if (targetSalesOffice) {
			const { salesOfficeName, salesOfficeId, salesOfficeNumber, isActive } = targetSalesOffice ?? {};
			const { address = '', lat = '', lng = '', description = '', category = '', marker_id } = targetSalesOffice?.details ?? {};
			const { phone, email } = parseContactInfo(description);
			const coordinates = (lat && lng)
				? `${ lat },${ lng }`
				: '';

			setSalesOffice({
				address,
				category,
				coordinates,
				directionsUrl: buildeDirectionsUrl(address, marker_id),
				email,
				isActive,
				markerId: marker_id,
				lat,
				lng,
				phone,
				salesOfficeId,
				salesOfficeNumber,
				salesOfficeName,
			});
		} else {
			setSalesOffice(null);
		}
	};

	const buildTitle = () => {
		let salesOfficeName = salesOffice?.salesOfficeName.toUpperCase();

		if (! salesOffice?.coordinates) {
			return (
				<div className='t-my-distributors-carousel__item__title'>{salesOfficeName}</div>
			);
		}
		const salesOfficeDirectionsUrl = salesOffice?.directionsUrl;
		return (
			<a href={salesOfficeDirectionsUrl} target="_blank" onClick={() => {
				triggerSalesOfficeAnalytics('other_click', salesOfficeName, salesOfficeDirectionsUrl);
			}} className='t-my-distributors-carousel__item__title' rel="noreferrer">{salesOfficeName}</a>
		);
	};

	const getOfficeSubName = () => {
		let name;

		if (salesOffice) {
			const { salesOfficeName, address } = salesOffice;
			name = (address)
				? address.split(',')[ 0 ]
				: salesOfficeName;
		}

		const doc = new DOMParser().parseFromString(name, 'text/html');

		return DOMPurify.sanitize(doc.documentElement.textContent || '');
	};

	const triggerSalesOfficeAnalytics = (interaction_type: SalesOfficeInteractionType, link_text, link_url) => {
		trackSalesOfficeInteraction(salesOffice?.address, interaction_type, link_text, link_url, salesOffice?.category, getOfficeSubName(), salesOffice?.salesOfficeName);
	};

	const triggerTrackFindSalesOfficeClick = (event, text:string, link: string) => {
		event.preventDefault();
		trackFindSalesOfficeClick(text, link);

		const target = link ? '_blank' : '_self';
		const newWindow = window.open(link, target, 'noopener,noreferrer');
		if (newWindow) {
			newWindow.opener = null;
		}
	};

	return (
		<div className={`t-my-sales-office ${ loading ? 't-full-block-skeleton' : '' } ${ salesOffice && ! salesOffice?.isActive ? 't-my-sales-office__item--disabled' : '' }`}>
			{salesOffice ? (
				<>
					{salesOffice?.lat && salesOffice?.lng && (
						<div className="t-my-sales-office__item__map">
							<a href={`https://www.google.com/maps/@${salesOffice?.lat},${salesOffice?.lng},${20}z`} target="_blank"
							   rel="noopener noreferrer">
								<img
									src={`https://maps.googleapis.com/maps/api/staticmap?center=${salesOffice?.lat},${salesOffice?.lng}&zoom=${14}&size=240x240&markers=color:red%7C${salesOffice?.lat},${salesOffice?.lng}&key=${apiKeyString}`}
									alt='Screenshot from Google Maps'/>
							</a>
						</div>
					)}

					<div className="t-my-sales-office__item">
						<div className='t-my-sales-office__item__info'>
							{salesOffice?.salesOfficeName && (
								buildTitle()
							)}
							{salesOffice?.category && (
								<div className='t-my-sales-office__item__category'>{salesOffice?.category}</div>
							)}
							{salesOffice?.address && (
								<div className='t-my-sales-office__item__address' dangerouslySetInnerHTML={ { __html: DOMPurify.sanitize(salesOffice?.address) } } />
							)}
							{salesOffice?.phone && (
								<div className={"t-my-sales-office__item__phone"}>
									{text.phone}: <a href={`tel:${salesOffice?.phone}`}
													 onClick={() => {triggerSalesOfficeAnalytics('click_to_call', `${text.phone}: ${salesOffice?.phone}`, `tel:${salesOffice.phone}`);
													 }} className='link-underline'>{salesOffice?.phone}</a>
								</div>
							)}
							{salesOffice?.email && (
								<div className={"t-my-sales-office__item__email"}>
									{text.email}: <a href={`mailto:${salesOffice?.email}`}
													 onClick={() => {triggerSalesOfficeAnalytics('click_to_email', `${text.email}: ${salesOffice?.email}`, `mailto:${salesOffice.email}`);
													 }} className='link-underline'>{salesOffice?.email}</a>
								</div>
							)}
						</div>
						<ul className="t-my-sales-office__item__links">
							{salesOffice?.coordinates && (
								<li>
									<Icon icon="map-location" />
									<a href={salesOffice?.directionsUrl} onClick={() => {
										triggerSalesOfficeAnalytics('get_directions_click', salesOffice?.salesOfficeName, salesOffice?.directionsUrl);
									}} target="_blank" className="btn--tertiary" rel="noreferrer">Get Directions</a>
								</li>
							)}

							<li>
								<Icon icon="trash-outline" />
								<a href="#" onClick={(event) => {
									event?.preventDefault();
									triggerDeleteConfirmation();
								}} className="btn--tertiary btn--delete">Remove</a>
							</li>
						</ul>
					</div>
				</>) : (
				<div className="t-my-sales-office__empty">
					<p className='fw-bold t-my-sales-office__empty--heading'>You currently have no Timken sales office selected.</p>
					<a href="#" onClick={(e) =>
						triggerTrackFindSalesOfficeClick(e, text.clickForSalesOffice, salesOfficeUrl)}
					   className='btn--tertiary' rel="noopener noreferrer">{text.clickForSalesOffice}</a>
				</div>
			)}
			<ConfirmationPrompt
				isOpen={isConfirmOpen}
				onConfirm={deleteSalesOffice}
				onCancel={() => setIsConfirmOpen(false)}
				message={confirmationMessage}/>
		</div>
	);
};

export default MySalesOffice;
